"use client";

import React from "react";
import { ShieldCheck, Award, Building2, House as Home, ArrowRight, MapPin } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function GuildfordIntro() {
  const shouldReduceMotion = useReducedMotion();
  const localScopes = [{
    title: "Town centre & period properties",
    description: "Specialist temporary access for Guildford's historic High Street, heritage facades, and tight-access town centre sites requiring careful pavement licensing.",
    icon: Building2
  }, {
    title: "Residential & conservation areas",
    description: "Tidy, secure domestic access for re-roofing, extensions, and renovations across Charlotteville, Onslow Village, Merrow, and surrounding conservation areas.",
    icon: Home
  }, {
    title: "Commercial & campus developments",
    description: "Heavy-duty scaffolding, stair towers, and edge protection for modern offices, business parks, and university-adjacent development schemes.",
    icon: MapPin
  }];
  return <section id="guildfordintro" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Main Content Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-foreground border-border gap-1.5 py-1 px-3">
                <MapPin className="size-3.5 text-primary" />
                <span>Guildford &amp; Surrey</span>
              </Badge>
              <Badge variant="secondary" className="gap-1.5 py-1 px-3">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>NASC &amp; CHAS accredited</span>
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
              Commercial and residential scaffolding in Guildford
            </h2>

            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Guildford and the surrounding Surrey area, including GU1 to GU4.
              </p>
              <p>
                BMF Scaffolding Ltd provides safe, fully managed scaffolding solutions across Guildford and the wider Surrey area. With over 20 years of hands-on industry leadership under Pete Wood, our teams bring practical site discipline, strict safety standards, and clear communication to every project.
              </p>
              <p>
                Guildford presents a diverse mix of access challenges. We regularly design and erect scaffolding for sensitive heritage buildings in the historic cobbled town centre, residential properties in local conservation zones, and busy commercial developments near the university and research parks.
              </p>
              <p>
                Holding full NASC and CHAS accreditations, we ensure every structure meets all statutory safety requirements, TG20 guidance, and local authority permitting standards from initial survey through to dismantle.
              </p>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  Request a Guildford site quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ProjectsCaseStudiesPage">
                  View recent projects
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Local Area Context & Assurance Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-5 flex flex-col gap-5">
            <div className="bg-card text-card-foreground border border-border rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <div className="p-2 rounded-md bg-accent text-primary">
                  <Award className="size-5" />
                </div>
                <div>
                  <h3 className="text-base font-default font-semibold text-foreground">
                    Direct experience &amp; verified safety
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Led by Pete Wood · 20+ years in access management
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-2.5">
                <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Full NASC audited scaffolding contractor</span>
                </div>
                <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>CHAS accredited contractor safety standards</span>
                </div>
                <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Public liability &amp; street licence coordination</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
                Guildford project access coverage
              </h3>
              
              {localScopes.map((scope, i) => {
              const Icon = scope.icon;
              return <Card key={scope.title} data-index={i} className="border-border shadow-xs">
                    <CardContent className="p-4 flex gap-3.5 items-start">
                      <div className="p-2 rounded-md bg-accent text-foreground shrink-0 mt-0.5">
                        <Icon className="size-4 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-sm font-default font-semibold text-foreground">
                          {scope.title}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {scope.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}